import { BayLevelEnum, IBayLevelData, ISizeSummary } from "open-vessel-definition";
import {
  IBayPattern,
  ISlotPattern,
  bayLevelHasCenterLineRow,
  getPairedIsoBay,
} from "@tedivo/tedivo-bay-grid-pure";
import { IValidationInput, IValidationOutput, IValidator } from "../types";

import { createDictionary } from "@tedivo/tedivo-pure-helpers";

const name = "centerLineRowValidator";
const okTranslationKey = "validators:bays.centerLineRow.ok";
const errorTranslationKey = "validators:bays.centerLineRow.error";

/** How a conflict is to be repaired */
export type TCenterLineRowRepair =
  /** The Bay-level's opt-out is the leftover: clear it so it follows the vessel again */
  | "clearOptOut"
  /** The row 00 slots are the leftovers: remove them */
  | "dropSlots";

export type TCenterLineRowRepairReason =
  /** The vessel has no centerline row at all, so the slots cannot be real */
  | "vesselHasNoRow00"
  /** The paired Bay-level has row 00 and keeps it, so the pair shares the row */
  | "pairedBayHasRow00"
  /** The paired Bay-level has no row 00 either, so the flag wins */
  | "pairedBayHasNoRow00";

/** A Bay-level that holds row 00 slots although it has no centerline row */
export interface ICenterLineRowConflict {
  isoBay: IBayPattern;
  level: BayLevelEnum;
  /** The row 00 slots found */
  slots: ISlotPattern[];
  repair: TCenterLineRowRepair;
  reason: TCenterLineRowRepairReason;
}

export interface ICenterLineRowOutput {
  conflicts: ICenterLineRowConflict[];
}

/**
 * Checks that row 00 slots only exist in Bay-levels that have the centerline row.
 *
 * Whether a Bay-level has the row is decided by the flags alone (vessel-wide
 * `sizeSummary.centerLineRow`, which a Bay-level can only opt out of with
 * `centerLineRow: 0`), never by the slots. A Bay-level without the row that still
 * holds row 00 slots cannot be drawn: nothing can derive CGs for those slots. This
 * validator reports the contradiction and says which side is the leftover:
 *
 * - vessel without centerline row: the slots are leftovers, drop them;
 * - the paired Bay-level has row 00 and keeps it: the opt-out is the leftover, clear it;
 * - otherwise the flag wins and the slots are dropped.
 */
export const centerLineRowValidator: IValidator<ICenterLineRowOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: centerLineRowValidatorFn,
  severity: "error",
  stage: "data",
};

export function centerLineRowValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<ICenterLineRowOutput> {
  const baysData = json.baysData || [];
  const sizeSummary: Pick<ISizeSummary, "centerLineRow"> = json.sizeSummary || {};
  const baysDict = createDictionary(baysData, (bl) => `${bl.isoBay}-${bl.level}`);

  const conflicts: ICenterLineRowConflict[] = [];

  baysData.forEach((bl) => {
    const slots = row00SlotsOf(bl);
    if (slots.length === 0) return;
    if (bayLevelHasCenterLineRow(sizeSummary, bl)) return;

    const { repair, reason } = decideRepair(bl, sizeSummary, baysDict);

    conflicts.push({ isoBay: bl.isoBay as IBayPattern, level: bl.level, slots, repair, reason });
  });

  const isValid = conflicts.length === 0;

  return {
    name,
    isValid,
    messageTranslationKey: isValid ? okTranslationKey : errorTranslationKey,
    result: { conflicts },
  };
}

/** The row 00 slots of a Bay-level */
export function row00SlotsOf(bl: Pick<IBayLevelData, "perSlotInfo">): ISlotPattern[] {
  return (Object.keys(bl.perSlotInfo || {}) as ISlotPattern[]).filter((pos) =>
    pos.startsWith("00"),
  );
}

function decideRepair(
  bl: IBayLevelData,
  sizeSummary: Pick<ISizeSummary, "centerLineRow">,
  baysDict: Record<string, IBayLevelData>,
): Pick<ICenterLineRowConflict, "repair" | "reason"> {
  if (!sizeSummary.centerLineRow) return { repair: "dropSlots", reason: "vesselHasNoRow00" };

  const pairedIsoBay = getPairedIsoBay(bl.isoBay as IBayPattern, bl.pairedBay);
  const paired = pairedIsoBay !== undefined ? baysDict[`${pairedIsoBay}-${bl.level}`] : undefined;

  if (
    paired !== undefined &&
    row00SlotsOf(paired).length > 0 &&
    bayLevelHasCenterLineRow(sizeSummary, paired)
  ) {
    return { repair: "clearOptOut", reason: "pairedBayHasRow00" };
  }

  return { repair: "dropSlots", reason: "pairedBayHasNoRow00" };
}
